import React, { useMemo, useEffect, useState } from 'react'
import { Section } from '../../../components/Page'
import { useAuth } from '../../../contexts/useAuthUser'
import { createCustomerPortal } from '../../../requests/billing'
import { handleError } from '../../../errors/handleError'
import './BillingSection.css'

export const BillingSection: React.FC = () => {
  const { authUser, subscription } = useAuth()
  const [portalUrl, setPortalUrl] = useState<string | null>(null)
  const [loadingPortal, setLoadingPortal] = useState(false)

  useEffect(() => {
    const fetchPortalUrl = async () => {
      if (!authUser?._id) {
        return
      }

      setLoadingPortal(true)
      try {
        const res = await createCustomerPortal()
        setPortalUrl(res.data.url)
      } catch (err) {
        handleError(err, 'Failed to load billing portal. Please try again later.')
      } finally {
        setLoadingPortal(false)
      }
    }

    fetchPortalUrl()
  }, [authUser?._id])

  const trialInfo = useMemo(() => {
    if (!authUser?.freeTrialEndsAt) {
      return null
    }

    const now = new Date()
    const trialEndDate = new Date(authUser.freeTrialEndsAt)

    // Check if trial is in the future
    if (trialEndDate <= now) {
      return null
    }

    // Calculate days remaining
    const diffTime = trialEndDate.getTime() - now.getTime()
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // Calculate total trial days (from createdAt to freeTrialEndsAt)
    const createdAt = authUser.createdAt ? new Date(authUser.createdAt) : now
    const totalTrialDays = Math.ceil((trialEndDate.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24))

    // Calculate progress percentage
    const daysUsed = totalTrialDays - daysRemaining
    const progressPercentage = Math.max(0, Math.min(100, (daysUsed / totalTrialDays) * 100))

    return {
      daysRemaining,
      totalTrialDays,
      progressPercentage,
      trialEndDate
    }
  }, [authUser])

  const handleViewPortal = () => {
    if (portalUrl) {
      window.location.href = portalUrl
    }
  }

  // Format subscription price
  const subscriptionPrice = useMemo(() => {
    if (!subscription?.items?.data?.[0]?.price) {
      return null
    }

    const price = subscription.items.data[0].price
    const amount = (price.unit_amount || 0) / 100
    const currency = price.currency?.toUpperCase() || 'USD'
    const interval = price.recurring?.interval || 'month'

    return {
      amount,
      currency,
      interval
    }
  }, [subscription])

  // If user has an active subscription, show subscription details
  if (subscription) {
    const currentPeriodEnd = new Date(subscription.items.data[0].current_period_end * 1000)

    return (
      <Section title="Billing">
        <div className="billing-subscription-container">
          <div className="billing-subscription-header">
            <h3>Current Subscription</h3>
            <span className={`billing-subscription-status billing-subscription-status-${subscription.status}`}>
              {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
            </span>
          </div>
          
          {subscriptionPrice && (
            <div className="billing-subscription-price">
              <span className="billing-subscription-amount">
                ${subscriptionPrice.amount.toFixed(2)} {subscriptionPrice.currency}
              </span>
              <span className="billing-subscription-interval">
                / {subscriptionPrice.interval}
              </span>
            </div>
          )}

          <div className="billing-subscription-period">
            <p>
              Next Billing Date: {currentPeriodEnd.toLocaleDateString()}
            </p>
            {subscription.cancel_at_period_end && (
              <p className="billing-subscription-canceling">
                Your subscription will cancel on {currentPeriodEnd.toLocaleDateString()}
              </p>
            )}
          </div>

          <button
            className="primary"
            type="button"
            onClick={handleViewPortal}
            disabled={loadingPortal || !portalUrl}
          >
            {loadingPortal ? 'Loading...' : 'Manage Subscription'}
          </button>
        </div>
      </Section>
    )
  }

  // If no subscription but has active trial, show trial info
  if (trialInfo) {
    return (
      <Section title="Billing">
        <div className="billing-trial-container">
          <div className="billing-trial-header">
            <h3>Free Trial</h3>
            <span className="billing-trial-days">
              {trialInfo.daysRemaining} {trialInfo.daysRemaining === 1 ? 'day' : 'days'} remaining
            </span>
          </div>
          <div className="billing-progress-bar-container">
            <div 
              className="billing-progress-bar-fill"
              style={{ width: `${trialInfo.progressPercentage}%` }}
            />
          </div>
          <p className="billing-trial-info">
            Your free trial ends on {trialInfo.trialEndDate.toLocaleDateString()}
          </p>
          <button
            className="primary"
            type="button"
            onClick={handleViewPortal}
            disabled={loadingPortal || !portalUrl}
          >
            {loadingPortal ? 'Loading...' : 'Upgrade'}
          </button>
        </div>
      </Section>
    )
  }

  // No subscription and no trial
  return (
    <Section title="Billing">
      <p>No active subscription or free trial.</p>
      <button
        className="primary"
        type="button"
        onClick={handleViewPortal}
        disabled={loadingPortal || !portalUrl}
      >
        {loadingPortal ? 'Loading...' : 'Upgrade'}
      </button>
    </Section>
  )
}
